import type {ReactElement} from 'react';
import {useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useAiContentVerification} from '@Hooks/useAiContentVerification';
import {AiContentVerifyDropzone} from './components/ai-content-verify-dropzone/ai-content-verify-dropzone';
import {AiContentVerifyFilePreview} from './components/ai-content-verify-file-preview/ai-content-verify-file-preview';
import {AiContentVerifyHowItWorks} from './components/ai-content-verify-how-it-works/ai-content-verify-how-it-works';
import {AiContentVerifyResult} from './components/ai-content-verify-result/ai-content-verify-result';
import {getAiContentVerifyProps, getClientSideRejection, getFailureMessage, getUploadLimits} from './ai-content-verify.library';
import {FILE_INPUT_ACCEPT} from './ai-content-verify.types';
import styles from './ai-content-verify.module.scss';

export function AiContentVerify(): ReactElement {
  const [pageProps] = useState(getAiContentVerifyProps);
  const [uploadLimits] = useState(() => {
    return getUploadLimits(pageProps);
  });
  const {verificationState, verifyFile, rejectFile, resetVerification} = useAiContentVerification();

  const onFileChosen = (file: File): void => {
    if (verificationState.isVerifying) {
      return;
    }

    const rejection = getClientSideRejection(file, pageProps);

    if (rejection) {
      rejectFile(file, rejection);

      return;
    }

    void verifyFile(file);
  };

  const getErrorMessage = (): string | null => {
    if (!verificationState.failure) {
      return null;
    }

    return getFailureMessage(verificationState.failure, uploadLimits);
  };

  const renderVerificationPanel = (): ReactElement => {
    if (verificationState.isVerifying && verificationState.file) {
      return renderCheckingState(verificationState.file);
    }

    if (verificationState.result && verificationState.file) {
      return <AiContentVerifyResult result={verificationState.result} file={verificationState.file} onCheckAnotherFile={resetVerification} />;
    }

    return (
      <AiContentVerifyDropzone
        limitsText={window.i18next.t('pmwjs_ai_verify_limits', {...uploadLimits})}
        errorMessage={getErrorMessage()}
        fileInputAccept={FILE_INPUT_ACCEPT}
        onFileChosen={onFileChosen}
      />
    );
  };

  return (
    <div className={styles.verify}>
      <header className={styles.verifyHeader}>
        <div className={styles.verifyEyebrow}>
          <Icon icon="icon-shield-check" size={IconSize.SIZE_ICON_16} ariaHidden />
          <Text val={window.i18next.t('pmwjs_ai_verify_eyebrow')} size={TextSize.SM} bold />
        </div>
        <h1 className={styles.verifyTitle}>{window.i18next.t('pmwjs_ai_verify_title')}</h1>
        <Text val={window.i18next.t('pmwjs_ai_verify_subtitle')} size={TextSize.LG} className={styles.verifySubtitle} />
      </header>
      <div className={styles.verifyPanel}>
        {renderVerificationPanel()}
        <Text val={window.i18next.t('pmwjs_ai_verify_privacy_note')} size={TextSize.SM} className={styles.verifyPrivacyNote} />
      </div>
      <AiContentVerifyHowItWorks />
    </div>
  );
}

function renderCheckingState(file: File): ReactElement {
  return (
    <div className={styles.verifyChecking}>
      <AiContentVerifyFilePreview file={file} />
      <div role="status" className={styles.verifyCheckingStatus}>
        <Icon icon="icon-upload" size={IconSize.SIZE_ICON_20} isLoading ariaHidden />
        <Text val={window.i18next.t('pmwjs_ai_verify_checking', {fileName: file.name})} size={TextSize.MD} bold className={styles.verifyCheckingText} />
      </div>
    </div>
  );
}
